import React from "react"
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { MapPin, Trash2 } from "lucide-react"
import { UserAddressDto } from "@/api/api-client"
import { useLanguage } from "@/hooks/useLanguage"

interface AddressCardProps {
  address: UserAddressDto
  onEdit: (address: UserAddressDto) => void
  onDelete: (id: number) => void
  onSetDefault: (id: number) => void
}

const Badge = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-bold uppercase text-primary-foreground ${className}`}
  >
    {children}
  </span>
)

export const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onEdit,
  onDelete,
  onSetDefault,
}) => {
  const { t } = useLanguage()
  const text = t.savedAddresses

  return (
    <Card className={address.isDefault ? "border-primary shadow-md" : ""}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-4 w-4 text-primary" />
            {address.receiverName}
          </CardTitle>
          {address.isDefault && (
            <Badge className="bg-primary hover:bg-primary">{text.default}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1 text-sm text-muted-foreground">
        <p>
          {address.address.street} {address.address.externalNumber}
          {address.address.internalNumber ? `, Int. ${address.address.internalNumber}` : ""}
        </p>
        <p>
          {address.address.neighborhood}, {address.address.zipCode}
        </p>
        <p>
          {address.address.city}, {address.address.state}
        </p>
        <p className="pt-2 font-medium text-foreground">{address.phone}</p>
      </CardContent>
      <CardFooter className="flex justify-between gap-2 border-t pt-2">
        <div className="flex gap-2">
          <Button
            variant="ghost"
            className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"
            size="sm"
            onClick={() => onEdit(address)}
          >
            {text.edit}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDelete(address.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        {!address.isDefault && (
          <Button variant="outline" className="bg-action text-action-foreground" size="sm" onClick={() => onSetDefault(address.id)}>
            {text.setDefault}
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}

